<template>
  <div>
    <div class="d-flex align-start flex-wrap mb-1">
      <div class="mr-4">
        <div class="text-h6">{{ sheet.name }}</div>
        <div class="caption grey--text text--darken-1">
          {{ $t('components.dataCheck.DataCheckSheetPanel.summaryLine', { series: seriesCount, rows: formatInteger(sheet.rows) }) }}
          <v-tooltip bottom max-width="320">
            <template v-slot:activator="{ on }">
              <v-icon small class="ml-1" v-on="on">mdi-help-circle-outline</v-icon>
            </template>
            <span>{{ $t('components.dataCheck.DataCheckSheetPanel.startDateHelp') }}</span>
          </v-tooltip>
        </div>
      </div>
      <v-spacer></v-spacer>
      <v-btn-toggle v-model="granularity" mandatory dense color="primary" class="mt-1">
        <v-btn v-for="gran in granularities" :key="gran" :value="gran" small :disabled="!isAvailable(gran)">
          {{ $t(`components.dataCheck.DataCheckSheetPanel.${gran}`) }}
        </v-btn>
      </v-btn-toggle>
    </div>

    <div class="d-flex align-center flex-wrap dc-controls mb-2">
      <v-select
        v-if="metricItems.length > 1"
        v-model="metricIndex"
        :items="metricItems"
        :label="$t('components.dataCheck.DataCheckSheetPanel.metric')"
        dense
        outlined
        hide-details
        class="dc-control"
      ></v-select>
      <v-select
        v-if="hasTotalView"
        v-model="variableView"
        :items="variableItems"
        :label="$t('components.dataCheck.DataCheckSheetPanel.variable')"
        dense
        outlined
        hide-details
        class="dc-control"
      ></v-select>
      <v-select
        v-if="filterItems.length"
        v-model="filterKey"
        :items="filterItems"
        :label="$t('components.dataCheck.DataCheckSheetPanel.filter')"
        dense
        outlined
        hide-details
        class="dc-control"
      ></v-select>
      <v-switch
        v-model="flaggedOnly"
        :label="$t('components.dataCheck.DataCheckSheetPanel.flaggedOnly')"
        :disabled="!flaggedCount"
        dense
        hide-details
        inset
        class="mt-0 mr-4"
      ></v-switch>
      <v-switch
        v-if="events.length"
        v-model="showEvents"
        :label="$t('components.dataCheck.DataCheckSheetPanel.showSpecialDates')"
        dense
        hide-details
        inset
        class="mt-0"
      ></v-switch>
    </div>

    <div v-if="fixedText" class="caption grey--text text--darken-1 mb-2">
      <v-icon small class="mr-1">mdi-filter-outline</v-icon>{{ $t('components.dataCheck.DataCheckSheetPanel.fixedDims', { list: fixedText }) }}
    </div>
    <v-alert v-if="sheet.bucket === 'week'" type="info" text dense class="mb-2">
      {{ $t('components.dataCheck.DataCheckSheetPanel.bucketedNotice') }}
    </v-alert>
    <v-alert v-if="issueTexts.length" type="warning" text dense border="left" class="mb-2">
      <div v-for="(text, index) in issueTexts" :key="index">{{ text }}</div>
    </v-alert>

    <div v-if="!rows.length" class="dc-empty">
      <v-icon size="48" color="grey lighten-1">mdi-chart-line-variant</v-icon>
      <p class="mt-3 mb-0 grey--text text--darken-1">{{ $t('components.dataCheck.DataCheckSheetPanel.noSeries') }}</p>
    </div>
    <template v-else>
      <div class="dc-section-title">
        {{ $t('components.dataCheck.DataCheckSheetPanel.coverageTitle') }}
        <v-tooltip bottom max-width="360">
          <template v-slot:activator="{ on }">
            <v-icon small class="ml-1" v-on="on">mdi-help-circle-outline</v-icon>
          </template>
          <span>{{ byBrand ? $t('components.dataCheck.DataCheckSheetPanel.coverageHelpByBrand') : $t('components.dataCheck.DataCheckSheetPanel.coverageHelp') }}</span>
        </v-tooltip>
      </div>
      <data-check-coverage-map :rows="coverageRows" :periods="periodLabels" :color="color" class="mb-6"></data-check-coverage-map>

      <div class="dc-section-title">{{ $t('components.dataCheck.DataCheckSheetPanel.seriesTitle') }}</div>
      <v-row dense>
        <v-col v-for="row in pagedRows" :key="row.index" cols="12" md="6">
          <v-lazy min-height="230" :options="{ threshold: 0 }">
            <data-check-series-card
              :title="row.label"
              :unit="row.series.u"
              :is-mean="row.series.a === 'mean'"
              :periods="periodLabels"
              :values="row.values"
              :total="row.total"
              :flags="row.flags"
              :events="eventAreas"
              :color="color"
            ></data-check-series-card>
          </v-lazy>
        </v-col>
      </v-row>
      <div v-if="rows.length > visibleCount" class="d-flex justify-center mt-3">
        <v-btn text color="primary" @click="visibleCount += pageSize">
          {{ $t('components.dataCheck.DataCheckSheetPanel.showMore', { count: rows.length - visibleCount }) }}
        </v-btn>
      </div>
    </template>
  </div>
</template>

<script>
import numbro from 'numbro'
import DataCheckCoverageMap from './DataCheckCoverageMap'
import DataCheckSeriesCard from './DataCheckSeriesCard'
import {
  GRANULARITIES,
  aggregate,
  buildAxis,
  computeSheetFlags,
  dateAxisIndex,
  defaultGranularity,
  granularityAvailable,
  periodKey,
  seriesLabel,
  seriesPoints,
  seriesTotal,
  totalSeries,
  warningCount
} from '@/utils/dataCheck'

const PAGE_SIZE = 24
// Sheets shown with one series per competitor (its media merged) instead of one per variable
const BY_BRAND_SHEETS = ['5. Competition Media']
// Sheets usually modelled as one total series: they open on the total of all their variables
const TOTAL_VIEW_SHEETS = ['6. Paid Influencers']

export default {
  props: {
    sheet: {
      type: Object,
      required: true
    },
    // Flags of every series, in the same order as sheet.series
    flags: {
      type: Array,
      required: true
    },
    range: {
      type: Object,
      required: true
    },
    kpiRange: {
      type: Object,
      default: null
    },
    events: {
      type: Array,
      default: () => []
    },
    color: {
      type: String,
      default: '#4DB6E7'
    }
  },
  data () {
    return {
      granularity: 'month',
      metricIndex: 0,
      variableView: 'series',
      filterKey: '',
      flaggedOnly: false,
      showEvents: false,
      visibleCount: PAGE_SIZE,
      pageSize: PAGE_SIZE,
      granularities: GRANULARITIES
    }
  },
  computed: {
    metricItems () {
      return this.sheet.metrics
        .map((metric, index) => ({ text: metric.col, value: index, used: metric.used }))
        .filter(item => item.used)
    },
    hasTotalView () {
      return TOTAL_VIEW_SHEETS.includes(this.sheet.name)
    },
    variableItems () {
      return [
        { text: this.$t('components.dataCheck.DataCheckSheetPanel.allVariables'), value: 'total' },
        { text: this.$t('components.dataCheck.DataCheckSheetPanel.byVariable'), value: 'series' }
      ]
    },
    showTotal () {
      return this.hasTotalView && this.variableView === 'total'
    },
    byBrand () {
      return BY_BRAND_SHEETS.includes(this.sheet.name) && this.sheet.keyCols.indexOf('Brand Name') >= 0
    },
    // Total of every variable of the selected metric (one per unit, or per competitor and unit),
    // flagged like any other series
    totals () {
      if (this.byBrand) return totalSeries(this.sheet, this.metricIndex, null, this.sheet.keyCols.indexOf('Brand Name'))
      if (!this.showTotal) return []
      return totalSeries(this.sheet, this.metricIndex, this.$t('components.dataCheck.DataCheckSheetPanel.allVariables'))
    },
    totalFlags () {
      return this.totals.length ? computeSheetFlags(Object.assign({}, this.sheet, { series: this.totals }), this.kpiRange) : []
    },
    seriesCount () {
      return new Set(this.sheet.series.map(series => series.k.join('\u001f'))).size
    },
    filterItems () {
      const items = []
      this.sheet.dims.forEach((dim, dimIndex) => {
        if (items.length) items.push({ divider: true })
        items.push({ header: dim.col })
        if (!dim.ok) {
          items.push({
            text: dim.reason === 'values'
              ? this.$t('components.dataCheck.DataCheckSheetPanel.filterUnavailableValues')
              : this.$t('components.dataCheck.DataCheckSheetPanel.filterUnavailableSize'),
            value: `unavailable:${dimIndex}`,
            disabled: true
          })
          return
        }
        dim.values.forEach((value, valueIndex) => {
          items.push({
            text: value === null ? this.$t('components.dataCheck.DataCheckSheetPanel.blankValue') : value,
            value: `${dimIndex}:${valueIndex}`
          })
        })
      })
      if (!items.length) return []
      return [{ text: this.$t('components.dataCheck.DataCheckSheetPanel.allData'), value: '' }, { divider: true }].concat(items)
    },
    filter () {
      if (!this.filterKey) return null
      const parts = this.filterKey.split(':')
      return { dim: Number(parts[0]), value: Number(parts[1]) }
    },
    axis () {
      return buildAxis(this.range, this.granularity)
    },
    periodLabels () {
      return this.axis.map(period => period.label)
    },
    dateToAxis () {
      return dateAxisIndex(this.sheet, this.axis, this.granularity)
    },
    // One row per series of the selected metric: values at the chosen granularity and filter
    allRows () {
      const rows = []
      const merged = this.showTotal || this.byBrand
      const source = merged ? this.totals : this.sheet.series
      const allFlags = merged ? this.totalFlags : this.flags
      source.forEach((series, index) => {
        if (series.m !== this.metricIndex) return
        const values = aggregate(series, seriesPoints(series, this.filter), this.axis.length, this.dateToAxis)
        // A filter hides the series with no rows for that value
        if (this.filter && values.every(v => v === null)) return
        const flags = allFlags[index] || []
        rows.push({
          index: merged ? `total-${index}` : index,
          series: series,
          label: seriesLabel(series),
          values: values,
          total: seriesTotal(values, series.a),
          flags: flags,
          warnings: warningCount(flags)
        })
      })
      return rows.sort((a, b) => (b.warnings - a.warnings) || (Math.abs(b.total || 0) - Math.abs(a.total || 0)))
    },
    flaggedCount () {
      return this.allRows.filter(row => row.warnings > 0).length
    },
    rows () {
      return this.flaggedOnly ? this.allRows.filter(row => row.warnings > 0) : this.allRows
    },
    pagedRows () {
      return this.rows.slice(0, this.visibleCount)
    },
    coverageRows () {
      const unnamed = this.$t('components.dataCheck.DataCheckSeriesCard.unnamed')
      return this.rows.map(row => ({
        label: (row.label || unnamed) + (row.series.u ? ` (${row.series.u})` : ''),
        values: row.values
      }))
    },
    // Special dates as [start label, end label] ranges of the current axis
    eventAreas () {
      if (!this.showEvents || !this.axis.length) return []
      const index = {}
      this.axis.forEach((period, i) => { index[period.key] = i })
      const areas = []
      this.events.forEach(event => {
        const start = index[periodKey(event.s, this.granularity)]
        const end = index[periodKey(event.e, this.granularity)]
        if (start === undefined || end === undefined) return
        areas.push({ start: this.axis[start].label, end: this.axis[end].label })
      })
      return areas
    },
    fixedText () {
      return this.sheet.fixed.map(dim => `${dim.col} = ${dim.value}`).join(' · ')
    },
    issueTexts () {
      const issues = this.sheet.issues || {}
      const texts = []
      const t = (key, values) => this.$t(`components.dataCheck.DataCheckSheetPanel.issue${key.charAt(0).toUpperCase()}${key.slice(1)}`, values)
      if (issues.missingColumns && issues.missingColumns.length) texts.push(t('missingColumns', { list: issues.missingColumns.join(', ') }))
      if (issues.badDates) texts.push(t('badDates', { count: this.formatInteger(issues.badDates) }))
      if (issues.textDates) texts.push(t('textDates', { count: this.formatInteger(issues.textDates) }))
      if (issues.nonNumeric) texts.push(t('nonNumeric', { count: this.formatInteger(issues.nonNumeric) }))
      if (issues.droppedSeries) texts.push(t('droppedSeries', { shown: this.sheet.series.length, hidden: issues.droppedSeries }))
      return texts
    }
  },
  methods: {
    isAvailable (gran) {
      return granularityAvailable(gran, this.sheet.bucket === 'week' ? 7 : this.sheet.g)
    },
    formatInteger (value) {
      return numbro(value).format({ thousandSeparated: true, mantissa: 0 })
    },
    reset () {
      this.granularity = defaultGranularity(this.sheet.g)
      this.metricIndex = this.metricItems.length ? this.metricItems[0].value : 0
      this.variableView = this.hasTotalView ? 'total' : 'series'
      this.filterKey = ''
      this.flaggedOnly = false
      this.showEvents = this.events.length > 0 && this.events.length <= 20
      this.visibleCount = PAGE_SIZE
    }
  },
  watch: {
    sheet: {
      immediate: true,
      handler () {
        this.reset()
      }
    },
    metricIndex () {
      this.visibleCount = PAGE_SIZE
    },
    variableView () {
      this.visibleCount = PAGE_SIZE
    },
    filterKey () {
      this.visibleCount = PAGE_SIZE
    },
    flaggedOnly () {
      this.visibleCount = PAGE_SIZE
    },
    flaggedCount (count) {
      if (!count) this.flaggedOnly = false
    }
  },
  components: {
    DataCheckCoverageMap,
    DataCheckSeriesCard
  }
}
</script>

<style scoped>
.dc-controls {
  gap: 12px;
}
.dc-control {
  max-width: 260px;
}
.dc-section-title {
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: rgba(0, 0, 0, 0.6);
  margin-bottom: 8px;
  display: flex;
  align-items: center;
}
.dc-empty {
  text-align: center;
  padding: 48px 0;
}
</style>
